const createUpdatedCols = (dataObj, initialCols, excludedKeys) => {
  const knownKeys = initialCols.map((col) => col.key);

  // Add fixed keys to the exclusion list which are from uibackend dto
  const additionalExcluded = new Set([
    "account_relationship_list",
    "dup_flag",
    "mcs_update_confirmed_flag",
    "orig_source_account_id",
    "orig_source_system_name",
    "prm_acc_type",
  ]);

  const combinedExcluded = new Set([...excludedKeys, ...additionalExcluded]);

  const dynamicKeys = Object.keys(dataObj).filter((key) => {
    const isKnown = knownKeys.includes(key);
    const isExcluded = combinedExcluded.has(key);
    return !isKnown && !isExcluded;
  });

  const dynamicColumns = dynamicKeys
    .map((key) => {
      const title = key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      return { title, key };
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  return [...initialCols, ...dynamicColumns];
};

export const StandardizedAccountsLabel = () => {
  const COLUMNS = [
    {
      title: "CAM ID",
      key: "camid",
    },
    {
      title: "Account Name",
      key: "account_name",
    },
    {
      title: "Street",
      key: "street",
    },
    {
      title: "Street Sup",
      key: "street_sup",
    },
    {
      title: "City",
      key: "city",
    },
    {
      title: "Postal Code",
      key: "postalcode",
    },
    {
      title: "State",
      key: "state",
    },
    {
      title: "Country",
      key: "country",
    },
    {
      title: "Domain",
      key: "domain",
    },
    {
      title: "Duns Number",
      key: "duns_number",
    },
    {
      title: "Global Parent ID",
      key: "gtm_global_parent_id",
    },
    {
      title: "Global Parent Name",
      key: "gtm_global_parent_name",
    },
    {
      title: "Global Sub ID",
      key: "gtm_global_sub_id",
    },
    {
      title: "Global Sub Name",
      key: "gtm_global_sub_name",
    },
    {
      title: "Created At",
      key: "sliding_created_at",
    },
    {
      title: "Last Updated",
      key: "sliding_last_updated",
    },
  ];

  return COLUMNS;
};

export const OrgEntityAccountsLabel = () => {
  const COLUMNS = [
    {
      title: "ORG ID",
      key: "org_entity_id",
    },
    {
      title: "Account Name",
      key: "account_name",
    },
    {
      title: "Street",
      key: "street",
    },
    {
      title: "Street Sup",
      key: "street_sup",
    },
    {
      title: "City",
      key: "city",
    },
    {
      title: "Postal Code",
      key: "postalcode",
    },
    {
      title: "State",
      key: "state",
    },
    {
      title: "Country",
      key: "country",
    },
    {
      title: "Domain",
      key: "domain",
    },
    {
      title: "Duns Number",
      key: "duns_number",
    },
    {
      title: "Global Parent ID",
      key: "gtm_global_parent_id",
    },
    {
      title: "Global Parent Name",
      key: "gtm_global_parent_name",
    },
    {
      title: "Global Sub ID",
      key: "gtm_global_sub_id",
    },
    {
      title: "Global Sub Name",
      key: "gtm_global_sub_name",
    },
    {
      title: "Created At",
      key: "sliding_created_at",
    },
    {
      title: "Last Updated",
      key: "sliding_last_updated",
    },
  ];

  return COLUMNS;
};

export const SourceSystemAccountsLabel = (dataObject = {}) => {
  const INITIAL_COLUMNS = [
    { title: "Src Account ID", key: "account_id" },
    { title: "Account Name", key: "account_name" },
    { title: "Address", key: "address" },
    { title: "Source System", key: "source_system" },
  ];

  const excludedKeys = new Set([
    "index",
    "address_key",
    "street",
    "street_sup",
    "city",
    "state",
    "postalcode",
    "country",
    "id",
    "cam_id",
    "sliding_created_at",
    "sliding_last_updated",
  ]);

  return createUpdatedCols(dataObject, INITIAL_COLUMNS, excludedKeys);
};

export const HMSSourceSystemAccountsLabel = (dataObject = {}) => {
  const INITIAL_COLUMNS = [
    { title: "Src Account ID", key: "account_id" },
    { title: "Account Name", key: "account_name" },
    { title: "Address", key: "address" },
    { title: "Source System", key: "source_system" },
  ];

  const excludedKeys = new Set([
    "org_entity",
    "source_system_accounts",
    "street",
    "city",
    "state",
    "postalcode",
    "country",
  ]);

  return createUpdatedCols(dataObject, INITIAL_COLUMNS, excludedKeys);
};

export const AddressLabel = () => {
  const COLUMNS = [
    {
      title: "Street",
      key: "street",
    },
    {
      title: "Street Sup",
      key: "street_sup",
    },
    {
      title: "City",
      key: "city",
    },
    {
      title: "Postal Code",
      key: "postalcode",
    },
    {
      title: "State",
      key: "state",
    },
    {
      title: "Country",
      key: "country",
    },
  ];

  return COLUMNS;
};

export const EAMSourceSystemAccountLabel = (dataObject = {}) => {
  const INITIAL_COLUMNS = [
    { title: "Src Account ID", key: "account_id" },
    { title: "Account Name", key: "account_name" },
    { title: "Address", key: "address" },
    { title: "Source System", key: "source_system" },
  ];

  const excludedKeys = new Set([
    "index",
    "address_key",
    "street",
    "street_sup",
    "city",
    "state",
    "postalcode",
    "country",
    "id",
    "cam_id",
    "sliding_created_at",
    "sliding_last_updated",
  ]);

  return createUpdatedCols(dataObject, INITIAL_COLUMNS, excludedKeys);
};

export const EnrichedAttributesLabel = () => {
  const COLUMNS = [
    {
      title: "DUNS",
      key: "duns",
    },
    {
      title: "BUSINESS NAME",
      key: "business_name",
    },
    {
      title: "STREET ADDRESS",
      key: "street_address",
    },
    {
      title: "STREET ADDRESS 2",
      key: "street_address2",
    },
    {
      title: "CITY",
      key: "city",
    },
    {
      title: "STATE",
      key: "state",
    },
    {
      title: "STATE ABBREVIATION",
      key: "state_abbr",
    },
    {
      title: "NATIONAL IDENTIFICATION NUMBER",
      key: "duns",
    },
    {
      title: "TELEPHONE NUMBER",
      key: "duns",
    },
    {
      title: "CHIEF EXECUTIVE OFFICER NAME",
      key: "duns",
    },
    {
      title: "CHIEF EXECUTIVE OFFICER TITLE",
      key: "duns",
    },
    {
      title: "LINE OF BUSINESS",
      key: "duns",
    },
    {
      title: "YEAR STARTED",
      key: "duns",
    },
    {
      title: "PREVIOUS D-U-N-S NUMBER",
      key: "duns",
    },
    {
      title: "FULL REPORT DATE",
      key: "duns",
    },
    {
      title: "HEADQUARTER/PARENT D-U-N-S NUMBER",
      key: "duns",
    },
    {
      title: "HEADQUARTER/PARENT BUSINESS NAME",
      key: "duns",
    },
    {
      title: "HEADQUARTER/PARENT STREET ADDRES",
      key: "duns",
    },
    {
      title: "HEADQUARTER/PARENT CITY NAME",
      key: "duns",
    },
    {
      title: "HEADQUARTER/PARENT STATE/PROVINC",
      key: "duns",
    },
    {
      title: "HEADQUARTER/PARENT COUNTRY NAME",
      key: "duns",
    },
    {
      title: "HEADQUARTER/PARENT D&B STATE/PROVINC",
      key: "duns",
    },
    {
      title: "HEADQUARTER/PARENT POSTAL CODE",
      key: "duns",
    },
    {
      title: "DOMESTIC ULTIMATE D-U-N-S NUMBER",
      key: "duns",
    },
    {
      title: "DOMESTIC ULTIMATE BUSINESS NAME",
      key: "duns",
    },
    {
      title: "DOMESTIC ULTIMATE STREET ADDRESS",
      key: "duns",
    },
    {
      title: "DOMESTIC ULTIMATE CITY NAME",
      key: "duns",
    },
    {
      title: "DOMESTIC ULTIMATE STATE/PROVINCE",
      key: "duns",
    },
    {
      title: "DOMESTIC ULTIMATE D&B STATE/PROVINCE",
      key: "duns",
    },
    {
      title: "DOMESTIC ULTIMATE POSTAL CODE",
      key: "duns",
    },
    {
      title: "GLOBAL ULTIMATE D-U-N-S NUMBER",
      key: "duns",
    },
    {
      title: "GLOBAL ULTIMATE BUSINESS NAME",
      key: "duns",
    },
    {
      title: "GLOBAL ULTIMATE STREET ADDRESS",
      key: "duns",
    },
    {
      title: "GLOBAL ULTIMATE CITY NAME",
      key: "duns",
    },
    {
      title: "GLOBAL ULTIMATE STATE/PROVINCE N",
      key: "duns",
    },
    {
      title: "GLOBAL ULTIMATE COUNTRY NAME",
      key: "duns",
    },
    {
      title: "GLOBAL ULTIMATE STATE/PROVINCE A",
      key: "duns",
    },
    {
      title: "GLOBAL ULTIMATE D&B COUNTRY CODE",
      key: "duns",
    },
    {
      title: "GLOBAL ULTIMATE POSTAL CODE",
      key: "duns",
    },
    {
      title: "URL DOMAIN NAME 1",
      key: "duns",
    },
  ];

  return COLUMNS;
};

export const GoldenRecordAccountsLabel = (dataObject = {}) => {
  const INITIAL_COLUMNS = [
    { title: "ORG ID", key: "org_entity_id" },
    { title: "Account Name", key: "org_name_1" },
    { title: "Street", key: "street_1" },
    { title: "City", key: "city" },
    { title: "Postal Code", key: "postalcode" },
    { title: "State", key: "state" },
    { title: "Country", key: "country" },
  ];

  const excludedKeys = new Set(["street_2", "Id"]);

  return createUpdatedCols(dataObject, INITIAL_COLUMNS, excludedKeys);
};

export const GoldenRecordSourceSystemAccountsLabel = (dataObject = {}) => {
  const INITIAL_COLUMNS = [
    { title: "Src Account ID", key: "account_id" },
    { title: "Account Name", key: "account_name" },
    { title: "Address", key: "address" },
    { title: "Source System", key: "source_system" },
  ];

  const excludedKeys = new Set(["index", "id"]);

  return createUpdatedCols(dataObject, INITIAL_COLUMNS, excludedKeys);
};

export const LAGGroupLabel = () => {
  const COLUMNS = [
    {
      title: "DOCID",
      key: "id",
    },
    {
      title: "LAGID",
      key: "lag_id",
    },
    {
      title: "CAMID",
      key: "cam_id",
    },
    {
      title: "ECCID",
      key: "ecc_id",
    },
    {
      title: "Account Name",
      key: "account_name",
    },
    {
      title: "Country",
      key: "country",
    },
    {
      title: "Domain",
      key: "domain",
    },
    {
      title: "Source System",
      key: "source_system",
    },
    {
      title: "Group",
      key: "group_key",
    },
    {
      title: "Related Group",
      key: "related_group_key",
    },
    {
      title: "Description",
      key: "group_description",
    },
    {
      title: "IMS Org Name",
      key: "ims_org_name",
    },
    {
      title: "IMS Org ID",
      key: "ims_org_id",
    },
  ];

  return COLUMNS;
};

export const GTMHInfoColumns = () => {
  const COLUMNS = [
    {
      title: "ID",
      key: "id",
    },
    {
      title: "Name",
      key: "sub_name",
    },
    {
      title: "Domain",
      key: "domain",
    },
    {
      title: "HQ DUNS",
      key: "duns_number",
    },
    {
      title: "Government",
      key: "government",
    },
    {
      title: "Industry",
      key: "industry",
    },
    {
      title: "Diversified",
      key: "diversified",
    },
    {
      title: "Sanctioned",
      key: "sanctioned",
    },
    {
      title: "Sub Count",
      key: "num_subs",
    },
    {
      title: "Status",
      key: "status",
    },
    {
      title: "Comment",
      key: "comment",
    },
    {
      title: "Workflow ID",
      key: "wf_id",
    },
    {
      title: "Change ID",
      key: "change_id",
    },
    {
      title: "Created By (Name)",
      key: "created_by_name",
    },
    {
      title: "Created By (ID)",
      key: "created_by",
    },
    {
      title: "Created Date",
      key: "created_date_time",
    },
    {
      title: "Modified By (Name)",
      key: "modified_by_name",
    },
    {
      title: "Modified By (ID)",
      key: "modified_by",
    },
    {
      title: "Modified Date",
      key: "modified_date_time",
    },
    {
      title: "Reviewed By (Name)",
      key: "reviewed_by_name",
    },
    {
      title: "Reviewed By (ID)",
      key: "reviewed_by",
    },
    {
      title: "Reviewed Date",
      key: "reviewed_date_time",
    },
  ];

  return COLUMNS;
};
